import { useState } from 'react'
import { Navigate, Link, useNavigate } from 'react-router-dom'
import { usePrinters } from '../hooks/usePrinters'
import RecoupCard from './home/RecoupCard'
import StatusRow from './home/StatusRow'
import StatsGrid from './home/StatsGrid'
import DailyTimeline from './home/DailyTimeline'
import { FAB } from '../components/ui'
import ThemeToggle from '../components/ThemeToggle'
import { type TimeRange, TIME_RANGE_OPTIONS } from '../lib/timeRange'
import { useRealtimeConsigneeSales } from '../hooks/useRealtimeConsigneeSales'

const iconBtnStyle = {
  width: 36,
  height: 36,
  background: 'var(--color-surface-2)',
  borderRadius: 'var(--radius-pill)',
  fontSize: '1.1rem',
  lineHeight: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textDecoration: 'none',
  color: 'inherit',
} as const

export default function Home() {
  const { data: printers, isLoading } = usePrinters()
  const navigate = useNavigate()
  const [range, setRange] = useState<TimeRange>('30d')
  // Live toast + cache refresh when mom logs a sale on her phone.
  useRealtimeConsigneeSales()

  if (isLoading) return <div style={{ padding: 'var(--space-6)' }}>Loading…</div>
  if (!printers || printers.length === 0) return <Navigate to="/onboarding" replace />

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 720, paddingBottom: 'calc(var(--nav-height-mobile) + var(--space-12))' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 'var(--space-6)',
      }}>
        <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, color: 'var(--color-text)' }}>Home</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <Link to="/journal" title="Journal" aria-label="Journal" style={iconBtnStyle}>
            📓
          </Link>
          <Link to="/calendar" title="Calendar" aria-label="Calendar" style={iconBtnStyle}>
            📅
          </Link>
          <Link to="/calculator" title="Pricing calculator" aria-label="Pricing calculator" style={iconBtnStyle}>
            🧮
          </Link>
          <ThemeToggle />
          <Link to="/maintenance" title="Maintenance" aria-label="Maintenance" style={iconBtnStyle}>
            🔧
          </Link>
          <Link to="/settings" title="Settings" aria-label="Settings" style={iconBtnStyle}>
            ⚙
          </Link>
        </div>
      </div>
      <RecoupCard />
      <StatusRow />

      <div
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-3)',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          Range:
        </span>
        {TIME_RANGE_OPTIONS.map((opt) => {
          const active = opt.value === range
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setRange(opt.value)}
              style={{
                padding: 'var(--space-1) var(--space-3)',
                borderRadius: 'var(--radius-pill)',
                border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
                background: active ? 'var(--color-primary-soft)' : 'transparent',
                color: active ? 'var(--color-primary-text)' : 'var(--color-text-muted)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: active ? 700 : 500,
                cursor: 'pointer',
              }}
              aria-pressed={active}
            >
              {opt.shortLabel}
            </button>
          )
        })}
      </div>

      <StatsGrid range={range} />
      <DailyTimeline range={range} />
      <FAB icon="+" onClick={() => navigate('/prints/new')} ariaLabel="Log a print" />
    </div>
  )
}
